import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from "react-toastify";
import { FaRegCheckCircle } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { FiCheck, FiEye, FiEyeOff, FiMail, FiUser, FiPhone, FiCalendar } from "react-icons/fi";
import GoogleLoginButton from "../components/GoogleLoginButton";
import logo from "../assets/logo.webp";
import styles from "../styles/login.module.css";
import signupStyles from "../styles/signup.module.css";

const Signup = () => {
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [signLoading, setSignLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [focused, setFocused] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setLoading(true);
      navigate("/");
    }
  }, [user, navigate]);

  const [formData, setFormData] = useState({
    name: "",
    birthdate: "",
    mobile: "",
    email: "",
    password: ""
  });

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value.trim().replace(/\s+/g, ' ')});
  };

  const handleSignup = async () => {
    setError("");

    if (!formData?.birthdate) {
      toast.error(<span className="toast">ادخل تاريخ ميلادك</span>);

    } else if (!formData?.mobile) {
      toast.error(<span className="toast">ادخل رقم هاتفك</span>);

    } else {
      setSignLoading(true);
      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, formData);
        setSuccess(true);
      } catch (error) {
        console.error("Signup Error:", error.code);

        let errorCode = "";
        let errorMessage = "An unknown error occurred. Please try again.";

        if (error.response?.data) {
          errorCode = error.response.data.data?.code || "";
          errorMessage = error.response.data.data?.message || error.response.data.message || errorMessage;
        }

        if (errorCode === "auth/email-already-exists") {
          errorMessage = "هذا البريد الإلكترونى مسجل بالفعل. من فضلك قم بتسجيل الدخول";
        }

        if (errorCode === "auth/internal-error" && errorMessage.includes('PASSWORD')) {
          errorMessage = "كلمة المرور يجب أن تكون من 10 إلى 20 عنصر وتحتوى على الأقل على حرف كبير و حرف صغير و رقم";
        }

        if (errorCode === "auth/invalid-email") {
          errorMessage = "البريد الإلكترونى غير صحيح"
        }

        setError(Array.isArray(errorMessage) ? errorMessage.join("، ") : errorMessage);
      } finally {
        setSignLoading(false);
      };
    }
  }

  // Floating label helper: label rises when the field is focused or filled
  const labelClass = (name) =>
    `${styles.floatLabel} ${focused === name || formData[name] ? styles.floatLabelUp : ''}`;

  const focusProps = (name) => ({
    onFocus: () => setFocused(name),
    onBlur: () => setFocused(f => (f === name ? "" : f)),
  });

  if (loading) return null;

  return (
    <div className={styles.page}>
      <div className={`${styles.card} ${signupStyles.card}`}>

        {/* Logo */}
        <img src={logo} alt="فزاكر" className={`${styles.logo} ${signupStyles.logo}`} />

        {success ? (
          <div className={signupStyles.success}>
            <FaRegCheckCircle className={signupStyles.successIcon} />
            <h2 className={styles.heading}>تم إنشاء حسابك بنجاح</h2>
            <p className={signupStyles.successText}>
              اذهب إلى بريدك الإلكترونى واضغط على رابط التأكيد حتى تتمكن من تسجيل الدخول
            </p>
            <button onClick={() => navigate("/login")} className={styles.submitBtn}>
              <FiCheck /> حسناً
            </button>
          </div>
        ) : (
          <>
            {/* Heading */}
            <h2 className={styles.heading}>أنشىء حساب جديد</h2>
            <p className={`${styles.subheading} ${signupStyles.subheading}`}>انضم إلينا واستمتع بتجربة تسوق مميزة</p>

            {/* Form */}
            <form className={`${styles.form} ${signupStyles.form}`} onSubmit={(e) => { e.preventDefault(); handleSignup(); }}>

              {/* Name */}
              <div className={styles.fieldWrap}>
                <label className={labelClass("name")}>الإسم *</label>
                <input
                  type="text"
                  name="name"
                  maxLength={30}
                  onChange={handleChange}
                  {...focusProps("name")}
                  autoComplete="name"
                  required
                  className={styles.input}
                />
                <FiUser className={styles.fieldIcon} />
              </div>

              {/* Birthdate */}
              <div className={`${styles.fieldWrap} ${signupStyles.dateWrap}`}>
                <label className={labelClass("birthdate")}>تاريخ الميلاد *</label>
                <DatePicker
                  selected={formData.birthdate ? new Date(formData.birthdate) : null}
                  onChange={(date) => {
                    setFormData(prev => ({ ...prev, birthdate: date ? format(date, 'yyyy-MM-dd') : "" }));
                  }}
                  onCalendarOpen={() => setFocused("birthdate")}
                  onCalendarClose={() => setFocused(f => (f === "birthdate" ? "" : f))}
                  dateFormat="dd/MM/yyyy"
                  showYearDropdown
                  dropdownMode="select"
                  maxDate={new Date()}
                  className={styles.input}
                />
                <FiCalendar className={styles.fieldIcon} />
              </div>

              {/* Mobile */}
              <div className={styles.fieldWrap}>
                <label className={labelClass("mobile")}>رقم الهاتف *</label>
                <input
                  type="tel"
                  name="mobile"
                  maxLength={20}
                  onChange={handleChange}
                  {...focusProps("mobile")}
                  autoComplete="tel"
                  required
                  className={styles.input}
                />
                <FiPhone className={styles.fieldIcon} />
              </div>

              {/* Email */}
              <div className={styles.fieldWrap}>
                <label className={labelClass("email")}>البريد الإلكترونى *</label>
                <input
                  type="email"
                  name="email"
                  maxLength={100}
                  onChange={handleChange}
                  {...focusProps("email")}
                  autoComplete="email"
                  required
                  className={styles.input}
                />
                <FiMail className={styles.fieldIcon} />
              </div>

              {/* Password */}
              <div className={`${styles.fieldWrap} ${signupStyles.full}`}>
                <label className={labelClass("password")}>كلمة المرور *</label>
                <input
                  type={showPass ? "text" : "password"}
                  name="password"
                  maxLength={20}
                  onChange={handleChange}
                  {...focusProps("password")}
                  autoComplete="new-password"
                  required
                  className={styles.input}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPass(s => !s)}
                  tabIndex={-1}
                >
                  {showPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              {/* Error */}
              {error && <p className={`${styles.error} ${signupStyles.full}`}>{error}</p>}

              {/* Submit */}
              <button type="submit" disabled={signLoading} className={`${styles.submitBtn} ${signupStyles.full}`}>
                {signLoading
                  ? <ImSpinner2 className={styles.spinner} />
                  : <FiCheck />
                }
                أنشىء حساب
              </button>
            </form>

            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <>
                <div className={styles.orDivider}><span>أو</span></div>
                <GoogleLoginButton onError={setError} text="signup_with" />
              </>
            )}

            <div className={styles.divider} />

            {/* Footer links */}
            <div className={styles.links}>
              <div className={styles.linkRow}>
                <span>لديك حساب بالفعل؟</span>
                <Link to="/login" className={styles.link}>سجّل دخولك</Link>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default Signup;
